import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    TextField,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {getTooltipButton} from '../../../patterns/components/button/button';

export const CustomCategoryModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).questionStore;
    const userStore = useContext(StoreContext).userStore;
    const companyLanguages = userStore.payload!.companyLanguages;
    const categoryInEditing = store.variables.openModal === 'edit-custom-category' ? store.variables.customCategory : undefined;

    const [textEnglish, setTextEnglish] = useState<string>(categoryInEditing?.textEnglish ?? '');
    const [textEstonian, setTextEstonian] = useState<string>(categoryInEditing?.textEstonian ?? '');
    const [textFinnish, setTextFinnish] = useState<string>(categoryInEditing?.textFinnish ?? '');
    const [textLatvian, setTextLatvian] = useState<string>(categoryInEditing?.textLatvian ?? '');
    const [textLithuanian, setTextLithuanian] = useState<string>(categoryInEditing?.textLithuanian ?? '');
    const [textPolish, setTextPolish] = useState<string>(categoryInEditing?.textPolish ?? '');
    const [textSwedish, setTextSwedish] = useState<string>(categoryInEditing?.textSwedish ?? '');
    const [textRussian, setTextRussian] = useState<string>(categoryInEditing?.textRussian ?? '');
    const [textJapanese, setTextJapanese] = useState<string>(categoryInEditing?.textJapanese ?? '');
    const [textVietnamese, setTextVietnamese] = useState<string>(categoryInEditing?.textVietnamese ?? '');
    const [isOnboarding, setIsOnboarding] = useState<boolean>(categoryInEditing?.isOnboarding ?? false);
    const [activeField, setActiveField] = useState<string | null>(null);

    const languageMap: Record<string, { state: string; setter: (value: string) => void; labelKey: string }> = {
        en: { state: textEnglish, setter: setTextEnglish, labelKey: 'categories.custom-category.text-english' },
        et: { state: textEstonian, setter: setTextEstonian, labelKey: 'categories.custom-category.text-estonian' },
        fi: { state: textFinnish, setter: setTextFinnish, labelKey: 'categories.custom-category.text-finnish' },
        lv: { state: textLatvian, setter: setTextLatvian, labelKey: 'categories.custom-category.text-latvian' },
        lt: { state: textLithuanian, setter: setTextLithuanian, labelKey: 'categories.custom-category.text-lithuanian' },
        pl: { state: textPolish, setter: setTextPolish, labelKey: 'categories.custom-category.text-polish' },
        sv: { state: textSwedish, setter: setTextSwedish, labelKey: 'categories.custom-category.text-swedish' },
        ru: { state: textRussian, setter: setTextRussian, labelKey: 'categories.custom-category.text-russian' },
        ja: { state: textJapanese, setter: setTextJapanese, labelKey: 'categories.custom-category.text-japanese' },
        vi: { state: textVietnamese, setter: setTextVietnamese, labelKey: 'categories.custom-category.text-vietnamese' },
    };

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{store.variables.openModal === 'edit-custom-category' ? store.label('categories.custom-category-modal.title.edit') : store.label('categories.custom-category-modal.title.add')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    {companyLanguages.map((language) => {

                        if (!languageMap[language]) {
                            return null;
                        }

                        return (
                            <div key={language} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <TextField
                                    label={store.label(languageMap[language].labelKey)}
                                    value={languageMap[language].state}
                                    onChange={(value) => {
                                        languageMap[language].setter(value);
                                        setActiveField(language);
                                    }}
                                    onFocus={() => setActiveField(language)}
                                    id={`custom-category-${language}`}
                                    name={`custom-category-${language}`}
                                    description={
                                        store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language)
                                            ? store.label(`common.validation.${store.checkText(languageMap[language].state, language)}`)
                                            : undefined
                                    }
                                    invalid={store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language) !== undefined}
                                />
                                {activeField === language && getTooltipButton({
                                    icon: 'translate',
                                    tooltip: store.label('questions.question-modal.translate'),
                                    onClick: async () => await store.translateTextForLanguages(language, languageMap[language].state, companyLanguages, languageMap),
                                })}
                            </div>
                        );
                    })}
                </div>
                <br/>
                <Check
                    label={store.label('categories.custom-category.is-onboarding')}
                    defaultChecked={isOnboarding}
                    onChange={(checked): void => setIsOnboarding(checked)}
                    id='custom-category-isOnboarding'
                    name='custom-category-isOnboarding'
                    value='custom-category-isOnboarding'
                />
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white"
                                text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveCustomCategory({
                                categoryId: categoryInEditing?.id ?? '',
                                categoryLanguages: {
                                    textEnglish: textEnglish !== '' ? textEnglish : undefined,
                                    textEstonian: textEstonian !== '' ? textEstonian : undefined,
                                    textFinnish: textFinnish !== '' ? textFinnish : undefined,
                                    textLatvian: textLatvian !== '' ? textLatvian : undefined,
                                    textLithuanian: textLithuanian !== '' ? textLithuanian : undefined,
                                    textPolish: textPolish !== '' ? textPolish : undefined,
                                    textSwedish: textSwedish !== '' ? textSwedish : undefined,
                                    textRussian: textRussian !== '' ? textRussian : undefined,
                                    textJapanese: textJapanese !== '' ? textJapanese : undefined,
                                    textVietnamese: textVietnamese !== '' ? textVietnamese : undefined,
                                },
                                isOnboarding,
                            })}/>
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
